import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import { ALBUM_FOLDER_UNIFORM_TAG_JERSEY_COLOR } from '../../../constants/album-folder-tags';
import CustomControl from '../CustomControl';
import { capitalize } from '../../../util/text';
import Label from '../Label';
import InfoCircleIcon from '../../icons/InfoCircleIcon';

const propTypes = {
  onColorToggle: PropTypes.func.isRequired,
  isColorSelected: PropTypes.func.isRequired,
  label: PropTypes.string,
  tip: PropTypes.node,
  className: PropTypes.string,
};

const JerseyColorSelector = ({
  onColorToggle,
  isColorSelected,
  label,
  tip,
  className,
}) => {
  const colorOptions = useMemo(
    () =>
      Object.values(ALBUM_FOLDER_UNIFORM_TAG_JERSEY_COLOR).map(color =>
        color.toUpperCase(),
      ),
    [],
  );

  const labelText = useMemo(() => {
    if (label) {
      return label;
    }

    return 'Select all the jersey colors you want to present this product to';
  }, [label]);

  const tipNode = useMemo(() => {
    if (tip) {
      return tip;
    }

    return (
      <p className="text-center mb-0">
        Color tags allow you to dynamically
        <br />
        present a version of your product to
        <br />
        buyer that matches their jersey.
        <br />
      </p>
    );
  }, [tip]);

  const classes = classNames({
    'jersey-color-selector': true,
    [className]: !!className,
  });

  if (colorOptions.length === 0) {
    return null;
  }

  return (
    <div className={classes}>
      <Label id="jersey-color-selector" optional>
        {labelText}
      </Label>
      <div className="jersey-color-selector-colors">
        {colorOptions.map(color => (
          <CustomControl
            type="checkbox"
            shape="squared"
            label={capitalize(color)}
            key={color}
            checked={isColorSelected(color)}
            onChange={() => onColorToggle(color)}
          />
        ))}
      </div>
      <div className="text-sm text-italic mt-1">
        Note: Product will be presented to all if colors are not chosen.{' '}
        <span
          data-tip=""
          data-for="jersey-color-selector-tip"
          data-effect="solid"
          data-place="top"
        >
          <InfoCircleIcon size={12} />

          <ReactTooltip id="jersey-color-selector-tip" className="tooltip">
            {tipNode}
          </ReactTooltip>
        </span>{' '}
      </div>
    </div>
  );
};

JerseyColorSelector.propTypes = propTypes;

export default JerseyColorSelector;
